import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Lottie from "lottie-react";
import { ChevronLeft, ChevronRight, Eye, EyeOff, Pause, Play } from "lucide-react";
import playButtonAnimation from "../assets/playbutton.json";
import { API_URL } from "../config/api";
import PackAssignDropdown from "./PackAssignDropdown";
import {
  EWSS_GRADES,
  TRAINING_CONSECUTIVE_FAIL_OPTIONS,
  TRAINING_DEFAULT_NO_EWSS_GAME_COUNT,
  TRAINING_MODE_FREE,
  TRAINING_MODE_QUEST,
  TRAINING_NO_EWSS_GAME_COUNT_OPTIONS,
  TRAINING_POTION_AID_FILTER_OPTIONS,
  TRAINING_SPEED_OPTIONS,
  PLAYED_COUNT_FILTER_MAX,
} from "../constants/entrainementSetup";
import {
  GROUPEMENT_MODE_OPTIONS,
  ILLUSTRATION_FILTER_OPTIONS,
} from "../constants/registerFilters";
import { filterTrainingWords, shuffleTrainingWords } from "../utils/entrainementWordFilter";
import {
  readTrainingSetupPreference,
  writeTrainingSetupPreference,
} from "../utils/trainingSetupPreference";
import {
  readTrainingHideWord,
  writeTrainingHideWord,
} from "../utils/trainingWordHidePreference";
import { getWordEwssDisplayGrade, renderGradeInLaurel } from "../utils/ewssGradeDisplay";
import { fetchTodayQuests } from "../utils/questApi";
import { reportQuestTrainingSession } from "../utils/questProgressReporter";
import {
  buildQuestTrainingWordPool,
  QUEST_TRAINING_WORD_DWELL_SECONDS,
} from "../utils/questTrainingWordPool";
import { maskDefinition } from "../utils/maskDefinition";
import { fetchWordIllustration } from "../utils/wordIllustration";
import { assignWordGroupement, fetchGroupements } from "../utils/groupementsApi";
import LiteraryExcerpt from "./LiteraryExcerpt";
import CountDownTimer from "./CountDownTimer";
import QuestTrainingResultModal from "./QuestTrainingResultModal";
import ToggleSwitch from "./ToggleSwitch";
import GroupementAssignDropdown from "./GroupementAssignDropdown";
import "./EntrainementVoc.css";

/** Tests style : mettre à true pour sauter l'écran de configuration. */
const AUTO_START_TRAINING = false;

const EMPTY_FILTER_OPTIONS = {
  auteurs: [],
  livres: [],
  years: [],
  byLivre: {},
  byAuteur: {},
  groupements: [],
  illustrationMots: [],
};

const CATEGORY_OPTIONS = [
  { key: "noyau", label: "Noyau actif" },
  { key: "favoris", label: "Mots favoris" },
  { key: "prioritaires", label: "Mots prioritaires" },
  { key: "paquet1", label: "Paquet 1" },
  { key: "paquet2", label: "Paquet 2" },
  { key: "paquet3", label: "Paquet 3" },
  { key: "paquet4", label: "Paquet 4" },
  { key: "horsPaquet", label: "Paquet 5" },
];

function sortFr(values = []) {
  return [...values].sort((a, b) => a.localeCompare(b, "fr", { sensitivity: "base" }));
}

function parseDateForSort(dateStr) {
  if (!dateStr || typeof dateStr !== "string") return 0;
  const m = dateStr.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return 0;
  return new Date(parseInt(m[3], 10), parseInt(m[2], 10) - 1, parseInt(m[1], 10)).getTime();
}

function parseTimeForSort(timeStr) {
  if (!timeStr || typeof timeStr !== "string") return 0;
  const parts = timeStr.trim().split(":");
  if (parts.length < 2) return 0;
  const hh = parseInt(parts[0], 10);
  const mm = parseInt(parts[1], 10);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return 0;
  return hh * 60 + mm;
}

function getLastGameEntry(stats) {
  const last5 = stats?.last5Games;
  if (!Array.isArray(last5) || !last5.length) return null;
  return [...last5].sort((a, b) => {
    const dateDiff = parseDateForSort(b.date) - parseDateForSort(a.date);
    if (dateDiff !== 0) return dateDiff;
    return parseTimeForSort(b.time) - parseTimeForSort(a.time);
  })[0];
}

function getLastSpellingGiven(stats) {
  const last = getLastGameEntry(stats);
  const given = typeof last?.given === "string" ? last.given.trim() : "";
  return given || null;
}

function splitMotDisplay(mot) {
  const chars = typeof mot === "string" ? Array.from(mot) : [];
  const initial = (chars[0] ?? "").toUpperCase();
  const rest = chars.slice(1).join("");
  const initialIsAccented = /[\u0300-\u036f]/.test(initial.normalize("NFD"));
  return { initial, rest, initialIsAccented };
}

function getTrainingMotsCaches(word) {
  const mot = typeof word?.mot === "string" ? word.mot.trim() : "";
  const caches = Array.isArray(word?.motsCaches)
    ? word.motsCaches.map((entry) => String(entry).trim()).filter(Boolean)
    : [];
  if (mot && !caches.some((entry) => entry.toLocaleLowerCase("fr-FR") === mot.toLocaleLowerCase("fr-FR"))) {
    return [mot, ...caches];
  }
  return caches.length ? caches : mot ? [mot] : [];
}

function Quadrant({ title, children, className = "", hideTitle = false }) {
  return (
    <section className={`entrainement-quadrant ${className}`.trim()}>
      {!hideTitle && title ? (
        <h3 className="entrainement-quadrant__title">{title}</h3>
      ) : null}
      <div className="entrainement-quadrant__body">{children}</div>
    </section>
  );
}

function TrainingSessionMotRow({
  hideTrainingWord,
  onHideTrainingWordChange,
  currentWordDisplay,
  groupements = [],
  groupementId = "",
  groupementBusy = false,
  onGroupementChange = () => {},
}) {
  return (
    <div className="entrainement-session__mot-row">
      <div
        className="entrainement-session__mot-hide"
        aria-label={hideTrainingWord ? "Afficher le mot" : "Masquer le mot"}
      >
        <Eye
          size={18}
          strokeWidth={2}
          aria-hidden="true"
          className={
            hideTrainingWord
              ? "entrainement-session__mot-hide-eye"
              : "entrainement-session__mot-hide-eye entrainement-session__mot-hide-eye--active"
          }
        />
        <ToggleSwitch
          compact
          className="entrainement-session__mot-hide-switch"
          checked={hideTrainingWord}
          onChange={onHideTrainingWordChange}
        />
        <EyeOff
          size={18}
          strokeWidth={2}
          aria-hidden="true"
          className={
            hideTrainingWord
              ? "entrainement-session__mot-hide-eye entrainement-session__mot-hide-eye--active"
              : "entrainement-session__mot-hide-eye"
          }
        />
      </div>
      <h3 className="entrainement__mot" aria-hidden={hideTrainingWord}>
        <span
          className={`entrainement__mot-text${hideTrainingWord ? " is-hidden" : ""}`}
        >
          <span
            className={`entrainement__mot-initial${
              currentWordDisplay.initialIsAccented
                ? " entrainement__mot-initial--accented"
                : ""
            }`}
          >
            {currentWordDisplay.initial}
          </span>
          {currentWordDisplay.rest}
        </span>
      </h3>
      <GroupementAssignDropdown
        className="entrainement-session__groupement"
        groupements={groupements}
        value={groupementId || ""}
        onChange={onGroupementChange}
        disabled={!groupements.length || groupementBusy}
      />
    </div>
  );
}

function TrainingSessionWordDetails({
  hideTrainingWord,
  definitionToShow,
  extraitToShow,
  currentWord,
  currentWordLastSpelling,
}) {
  return (
    <>
      <p className="entrainement-session__definition">
        <strong>Définition :</strong> {definitionToShow}
      </p>
      <p className="entrainement-session__extrait">
        <strong>Extrait :</strong> <LiteraryExcerpt text={extraitToShow} />
      </p>
      <div className="entrainement-session__attribution">
        <p className="entrainement-session__source">
          <em>
            {currentWord.livre}, {currentWord.auteur} ({currentWord.date})
          </em>
        </p>
        <p
          className={`entrainement-session__last-spelling${
            hideTrainingWord ? " is-hidden" : ""
          }`}
          aria-hidden={hideTrainingWord}
        >
          <strong>Dernièrement orthographié :</strong>{" "}
          {currentWordLastSpelling ?? "—"}
        </p>
      </div>
    </>
  );
}

function EntrainementSelect({
  value,
  onChange,
  options = [],
  disabled = false,
  ariaLabel,
  compact = false,
  tiny = false,
  className = "",
}) {
  const normalized = options.map((option) =>
    typeof option === "string" || typeof option === "number"
      ? { value: String(option), label: String(option) }
      : { value: String(option.value), label: option.label }
  );
  const sizeClass = tiny
    ? " entrainement-select-dd--tiny"
    : compact
      ? " entrainement-select-dd--compact"
      : "";

  return (
    <div className={`entrainement-select-dd${sizeClass}${className ? ` ${className}` : ""}`}>
      <PackAssignDropdown
        options={normalized}
        value={String(value ?? "")}
        onChange={onChange}
        disabled={disabled}
        ariaLabel={ariaLabel}
        matchTriggerWidth
        openBelow
        menuMaxHeight={200}
        menuZIndex={2147483600}
        menuClassName={`entrainement-select-dd__menu${
          tiny
            ? " entrainement-select-dd__menu--tiny"
            : compact
              ? " entrainement-select-dd__menu--compact"
              : ""
        }`}
      />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="entrainement-field">
      <span className="entrainement-field__label">{label}</span>
      {children}
    </div>
  );
}

function CheckOption({ label, checked, onChange, disabled = false, full = false }) {
  return (
    <label
      className={[
        "entrainement-chip",
        "entrainement-chip--round",
        checked ? "entrainement-chip--checked" : "",
        full ? "entrainement-chip--full" : "",
        disabled ? "entrainement-chip--disabled" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <input
        type="checkbox"
        className="entrainement-chip__input"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
      <span className="entrainement-chip__marker" aria-hidden="true" />
      <span className="entrainement-chip__label">{label}</span>
    </label>
  );
}

function Skel({ className = "", style }) {
  return <span className={`entrainement-skel ${className}`.trim()} style={style} />;
}

function SkelField({ labelWidth, select = "tiny" }) {
  return (
    <div className="entrainement-field">
      <Skel className="entrainement-skel--field-label" style={{ width: labelWidth }} />
      <Skel
        className={
          select === "compact"
            ? "entrainement-skel--select-compact"
            : select === "full"
              ? "entrainement-skel--select"
              : "entrainement-skel--select-tiny"
        }
      />
    </div>
  );
}

function SkelChip({ full = false, style }) {
  return (
    <span
      className={`entrainement-chip entrainement-chip--round entrainement-skel${
        full ? " entrainement-chip--full" : ""
      }`}
      style={style}
    />
  );
}

function EntrainementSetupSkeleton() {
  return (
    <div className="entrainement-setup entrainement-setup--skeleton" aria-hidden="true">
      <div className="entrainement-setup__intro">
        <h2 className="entrainement-setup__heading">Entraînement libre</h2>
      </div>

      <div className="entrainement-setup__board">
        <Quadrant hideTitle className="entrainement-quadrant--tl">
          <h3 className="entrainement-quadrant__title">
            <Skel className="entrainement-skel--quad-title" style={{ width: "92px" }} />
          </h3>
          <div className="entrainement-ewss-grades">
            <SkelField labelWidth="72px" />
            <SkelField labelWidth="108px" />
            <SkelField labelWidth="112px" />
          </div>
          <div className="entrainement-chip-grid entrainement-chip-grid--ewss-flags">
            <SkelChip />
            <SkelChip />
          </div>
          <Skel className="entrainement-skel--select-tiny" />
        </Quadrant>

        <Quadrant hideTitle className="entrainement-quadrant--tr">
          <h3 className="entrainement-quadrant__title">
            <Skel className="entrainement-skel--quad-title" style={{ width: "98px" }} />
          </h3>
          <div className="entrainement-chip-grid entrainement-chip-grid--categories">
            <SkelChip full />
            <div className="entrainement-consecutive-fails">
              <SkelChip />
              <Skel className="entrainement-skel--select-compact" />
            </div>
            {CATEGORY_OPTIONS.map(({ key }) => (
              <SkelChip key={key} />
            ))}
            <div className="entrainement-lecture-row entrainement-illustration-filter">
              <SkelField labelWidth="72px" select="compact" />
            </div>
          </div>
        </Quadrant>

        <Quadrant hideTitle className="entrainement-quadrant--bl">
          <h3 className="entrainement-quadrant__title">
            <Skel className="entrainement-skel--quad-title" style={{ width: "64px" }} />
          </h3>
          <SkelField labelWidth="68px" select="full" />
          <SkelField labelWidth="56px" select="full" />
          <SkelField labelWidth="118px" select="full" />
        </Quadrant>

        <Quadrant hideTitle className="entrainement-quadrant--br">
          <div className="entrainement-quadrant-rubric">
            <h4 className="entrainement-quadrant-rubric__title">
              <Skel className="entrainement-skel--quad-title" style={{ width: "68px" }} />
            </h4>
            <div className="entrainement-potions-used">
              <SkelChip />
              <Skel className="entrainement-skel--select-compact" />
            </div>
          </div>
          <div className="entrainement-quadrant-rubric">
            <h4 className="entrainement-quadrant-rubric__title">
              <Skel className="entrainement-skel--quad-title" style={{ width: "168px" }} />
            </h4>
            <Skel className="entrainement-skel--select-compact" />
          </div>
          <div className="entrainement-quadrant-rubric entrainement-quadrant-rubric--played-count">
            <h4 className="entrainement-quadrant-rubric__title">
              <Skel className="entrainement-skel--quad-title" style={{ width: "148px" }} />
            </h4>
            <div className="entrainement-played-count-range">
              <Skel className="entrainement-skel--input" />
              <Skel className="entrainement-skel--sep" />
              <Skel className="entrainement-skel--input" />
            </div>
          </div>
          <div className="entrainement-quadrant-rubric entrainement-quadrant-rubric--lecture">
            <h4 className="entrainement-quadrant-rubric__title">
              <Skel className="entrainement-skel--quad-title" style={{ width: "72px" }} />
            </h4>
            <div className="entrainement-lecture-row">
              <SkelField labelWidth="124px" select="compact" />
              <SkelField labelWidth="78px" select="compact" />
            </div>
          </div>
        </Quadrant>
      </div>

      <p className="entrainement-setup__count">
        <Skel className="entrainement-skel--count" />
      </p>
    </div>
  );
}

function RadioOption({ name, label, checked, onChange }) {
  return (
    <label
      className={`entrainement-chip entrainement-chip--round${
        checked ? " entrainement-chip--checked" : ""
      }`}
    >
      <input
        type="radio"
        className="entrainement-chip__input"
        name={name}
        checked={checked}
        onChange={onChange}
      />
      <span className="entrainement-chip__marker" aria-hidden="true" />
      <span className="entrainement-chip__label">{label}</span>
    </label>
  );
}

export default function EntrainementVoc({
  motsInitial = [],
  onClose,
  embedded = false,
  onPhaseChange,
  onRegisterStopSession,
  trainingMode = TRAINING_MODE_FREE,
  onTrainingModeChange,
  onQuestTrainingAvailableChange,
}) {
  const [words, setWords] = useState(motsInitial);
  const [packsSummary, setPacksSummary] = useState(null);
  const [filterOptions, setFilterOptions] = useState(EMPTY_FILTER_OPTIONS);
  const [loading, setLoading] = useState(embedded && motsInitial.length === 0);
  const [loadError, setLoadError] = useState("");
  const [setup, setSetup] = useState(() => readTrainingSetupPreference());
  const [phase, setPhase] = useState("setup");
  const [questSessionAvailable, setQuestSessionAvailable] = useState(false);
  const [activeSessionMode, setActiveSessionMode] = useState(TRAINING_MODE_FREE);
  const [sessionWords, setSessionWords] = useState([]);
  const [sessionIndex, setSessionIndex] = useState(0);
  const [sessionQueue, setSessionQueue] = useState([]);
  const [isPaused, setIsPaused] = useState(false);
  const [dwellSecondsLeft, setDwellSecondsLeft] = useState(QUEST_TRAINING_WORD_DWELL_SECONDS);
  const [abandonOpen, setAbandonOpen] = useState(false);
  const [resultModal, setResultModal] = useState(null);
  const [wordMarks, setWordMarks] = useState({});
  const [hideTrainingWord, setHideTrainingWord] = useState(() => readTrainingHideWord());
  const [groupements, setGroupements] = useState([]);
  const [groupementBusy, setGroupementBusy] = useState(false);
  const [illustrationMots, setIllustrationMots] = useState(() => new Set());
  const intervalRef = useRef(null);
  const lottieRef = useRef(null);
  const illustrationCacheRef = useRef(new Map());
  const [currentIllustration, setCurrentIllustration] = useState(null);
  const questFinishingRef = useRef(false);
  const pendingLeaveRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetchGroupements()
      .then((items) => {
        if (cancelled) return;
        setGroupements(
          [...items].sort((a, b) =>
            String(a.title || "").localeCompare(String(b.title || ""), "fr", {
              sensitivity: "base",
            })
          )
        );
      })
      .catch(() => {
        if (!cancelled) setGroupements([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!embedded || motsInitial.length > 0) return undefined;

    let cancelled = false;

    const loadData = async () => {
      setLoading(true);
      setLoadError("");
      try {
        let wordsData = [];
        let packsData = null;
        let optionsData = {};

        const setupRes = await fetch(`${API_URL}/training/setup`);
        if (setupRes.ok) {
          const data = await setupRes.json();
          wordsData = Array.isArray(data.words) ? data.words : [];
          packsData = data.packsSummary ?? null;
          optionsData = data.filterOptions || {};
        } else {
          const [wordsRes, packsRes, optionsRes] = await Promise.all([
            fetch(`${API_URL}/mots`),
            fetch(`${API_URL}/packs`),
            fetch(`${API_URL}/mots-liste-detail/filter-options`),
          ]);

          const wordsPayload = await wordsRes.json().catch(() => []);
          const packsPayload = await packsRes.json().catch(() => null);
          const optionsPayload = await optionsRes.json().catch(() => ({}));

          if (!wordsRes.ok) {
            throw new Error(
              wordsPayload.message || "Impossible de charger les mots pour l'entraînement."
            );
          }

          wordsData = Array.isArray(wordsPayload) ? wordsPayload : [];
          packsData = packsPayload;
          optionsData = optionsPayload;
        }

        if (!cancelled) {
          setWords(wordsData);
          setPacksSummary(packsData);
          setFilterOptions({ ...EMPTY_FILTER_OPTIONS, ...optionsData });
          if (Array.isArray(optionsData.illustrationMots)) {
            setIllustrationMots(new Set(optionsData.illustrationMots));
          }
        }
      } catch (err) {
        if (!cancelled) {
          const message = err.message || "";
          const isNetworkError =
            message.includes("Failed to fetch") ||
            message.includes("NetworkError") ||
            message.includes("Load failed");
          setLoadError(
            isNetworkError
              ? "Impossible de joindre le serveur API (127.0.0.1:3060). Vérifiez qu'il est bien lancé."
              : message || "Erreur de connexion au serveur."
          );
          setWords([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadData();
    return () => {
      cancelled = true;
    };
  }, [embedded, motsInitial.length]);

  useEffect(() => {
    if (embedded && motsInitial.length === 0) return undefined;

    let cancelled = false;
    fetch(`${API_URL}/mots-liste-detail/filter-options`)
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok || cancelled || !Array.isArray(data.illustrationMots)) return;
        setIllustrationMots(new Set(data.illustrationMots));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [embedded, motsInitial.length]);

  useEffect(() => {
    if (motsInitial.length > 0) {
      setWords(motsInitial);
    }
  }, [motsInitial]);

  useEffect(() => {
    writeTrainingSetupPreference(setup);
  }, [setup]);

  const refreshQuestSessionAvailability = useCallback(async () => {
    try {
      const today = await fetchTodayQuests();
      const available = Boolean(today?.found && today.trainingQuestSessionAvailable);
      setQuestSessionAvailable(available);
      onQuestTrainingAvailableChange?.(available);
      if (!available) {
        onTrainingModeChange?.((prev) =>
          prev === TRAINING_MODE_QUEST ? TRAINING_MODE_FREE : prev
        );
      }
    } catch {
      setQuestSessionAvailable(false);
      onQuestTrainingAvailableChange?.(false);
      onTrainingModeChange?.((prev) =>
        prev === TRAINING_MODE_QUEST ? TRAINING_MODE_FREE : prev
      );
    }
  }, [onTrainingModeChange, onQuestTrainingAvailableChange]);

  useEffect(() => {
    refreshQuestSessionAvailability();
  }, [refreshQuestSessionAvailability]);

  const isQuestModeSelected = trainingMode === TRAINING_MODE_QUEST;
  const isQuestSessionActive =
    phase === "session" && activeSessionMode === TRAINING_MODE_QUEST;

  const filteredWords = useMemo(
    () => filterTrainingWords(words, setup, packsSummary, illustrationMots),
    [words, setup, packsSummary, illustrationMots]
  );

  const filteredCount = filteredWords.length;

  const yearOptions = useMemo(() => {
    const years = new Set(filterOptions.years || []);
    if (setup.livre && filterOptions.byLivre?.[setup.livre]?.years) {
      filterOptions.byLivre[setup.livre].years.forEach((y) => years.add(y));
    }
    if (setup.auteur && filterOptions.byAuteur?.[setup.auteur]?.years) {
      filterOptions.byAuteur[setup.auteur].years.forEach((y) => years.add(y));
    }
    return sortFr([...years].map(String));
  }, [filterOptions, setup.livre, setup.auteur]);

  const livreOptions = useMemo(() => {
    if (setup.auteur && filterOptions.byAuteur?.[setup.auteur]?.livres) {
      return sortFr(filterOptions.byAuteur[setup.auteur].livres);
    }
    return sortFr(filterOptions.livres || []);
  }, [filterOptions, setup.auteur]);

  const auteurOptions = useMemo(() => {
    if (setup.livre && filterOptions.byLivre?.[setup.livre]?.auteur) {
      return [filterOptions.byLivre[setup.livre].auteur];
    }
    return sortFr(filterOptions.auteurs || []);
  }, [filterOptions, setup.livre]);

  const patchSetup = useCallback((patch) => {
    setSetup((prev) => ({ ...prev, ...patch }));
  }, []);

  const toggleCategory = useCallback((key, checked) => {
    setSetup((prev) => ({
      ...prev,
      categories: { ...prev.categories, [key]: checked },
    }));
  }, []);

  const advanceSession = useCallback(() => {
    if (activeSessionMode !== TRAINING_MODE_QUEST && setup.withRepetition) {
      setSessionIndex(() => Math.floor(Math.random() * Math.max(sessionWords.length, 1)));
      return;
    }

    setSessionQueue((prevQueue) => {
      if (!prevQueue.length) {
        if (activeSessionMode === TRAINING_MODE_QUEST) {
          return prevQueue;
        }
        const queue = shuffleTrainingWords(sessionWords);
        const [next, ...rest] = queue;
        const nextIndex = sessionWords.findIndex((w) => w.mot === next?.mot);
        if (nextIndex >= 0) setSessionIndex(nextIndex);
        return rest;
      }

      const [next, ...rest] = prevQueue;
      const nextIndex = sessionWords.findIndex((w) => w.mot === next?.mot);
      if (nextIndex >= 0) setSessionIndex(nextIndex);
      return rest;
    });
  }, [sessionWords, setup.withRepetition, activeSessionMode]);

  const syncSessionQueueFromIndex = useCallback(
    (index) => {
      if (setup.withRepetition || !sessionWords.length) return;

      const remaining = [];
      for (let i = 1; i < sessionWords.length; i += 1) {
        remaining.push(sessionWords[(index + i) % sessionWords.length]);
      }
      setSessionQueue(remaining);
    },
    [sessionWords, setup.withRepetition]
  );

  const goToPreviousWord = useCallback(() => {
    if (sessionWords.length <= 1) return;

    const nextIndex = (sessionIndex - 1 + sessionWords.length) % sessionWords.length;
    setSessionIndex(nextIndex);
    syncSessionQueueFromIndex(nextIndex);
  }, [sessionIndex, sessionWords.length, syncSessionQueueFromIndex]);

  const goToNextWord = useCallback(() => {
    if (sessionWords.length <= 1) return;

    const nextIndex = (sessionIndex + 1) % sessionWords.length;
    setSessionIndex(nextIndex);
    syncSessionQueueFromIndex(nextIndex);
  }, [sessionIndex, sessionWords.length, syncSessionQueueFromIndex]);

  useEffect(() => {
    // En session de quête, l'index du mot est piloté par le chrono unique (voir ticker).
    if (isQuestSessionActive) return undefined;
    if (phase !== "session" || !sessionWords.length || isPaused) return undefined;

    intervalRef.current = setInterval(advanceSession, setup.speedSeconds * 1000);
    return () => clearInterval(intervalRef.current);
  }, [
    phase,
    sessionWords,
    setup.speedSeconds,
    advanceSession,
    isPaused,
    sessionIndex,
    isQuestSessionActive,
  ]);

  const resetQuestRuntimeState = useCallback(() => {
    questFinishingRef.current = false;
    pendingLeaveRef.current = null;
    setAbandonOpen(false);
    setDwellSecondsLeft(QUEST_TRAINING_WORD_DWELL_SECONDS);
  }, []);

  const startFreeSession = useCallback(() => {
    if (!filteredWords.length) return;

    const shuffled = shuffleTrainingWords(filteredWords);
    setActiveSessionMode(TRAINING_MODE_FREE);
    resetQuestRuntimeState();
    setWordMarks({});
    setSessionWords(shuffled);
    setSessionIndex(0);
    setSessionQueue(setup.withRepetition ? [] : shuffled.slice(1));
    setIsPaused(false);
    setPhase("session");
    onPhaseChange?.("session");
  }, [filteredWords, setup.withRepetition, onPhaseChange, resetQuestRuntimeState]);

  const startQuestSession = useCallback(() => {
    if (!questSessionAvailable) return;

    const pool = buildQuestTrainingWordPool(words);
    if (!pool.length) return;

    setActiveSessionMode(TRAINING_MODE_QUEST);
    resetQuestRuntimeState();
    setWordMarks({});
    setDwellSecondsLeft(QUEST_TRAINING_WORD_DWELL_SECONDS);
    setSessionWords(pool);
    setSessionIndex(0);
    setSessionQueue(pool.slice(1));
    setIsPaused(false);
    setPhase("session");
    onPhaseChange?.("session");
  }, [questSessionAvailable, words, onPhaseChange, resetQuestRuntimeState]);

  const startSession = useCallback(() => {
    if (isQuestModeSelected) {
      startQuestSession();
      return;
    }
    startFreeSession();
  }, [isQuestModeSelected, startQuestSession, startFreeSession]);

  useEffect(() => {
    if (!AUTO_START_TRAINING) return;
    if (loading || loadError || phase !== "setup") return;
    if (!filteredWords.length) return;
    startFreeSession();
  }, [loading, loadError, phase, filteredWords.length, startFreeSession]);

  const finishQuestSession = useCallback(async () => {
    if (questFinishingRef.current) return;
    questFinishingRef.current = true;

    clearInterval(intervalRef.current);
    intervalRef.current = null;
    setAbandonOpen(false);

    await reportQuestTrainingSession({ validated: true });
    await refreshQuestSessionAvailability();
    setResultModal({ validated: true });
  }, [refreshQuestSessionAvailability]);

  const goToNextQuestWord = useCallback(() => {
    if (!isQuestSessionActive || dwellSecondsLeft > 0 || questFinishingRef.current) return;

    if (sessionIndex >= sessionWords.length - 1) {
      finishQuestSession();
      return;
    }

    const nextIndex = sessionIndex + 1;
    setSessionIndex(nextIndex);
    syncSessionQueueFromIndex(nextIndex);
    setDwellSecondsLeft(QUEST_TRAINING_WORD_DWELL_SECONDS);
  }, [
    isQuestSessionActive,
    dwellSecondsLeft,
    sessionIndex,
    sessionWords.length,
    syncSessionQueueFromIndex,
    finishQuestSession,
  ]);

  useEffect(() => {
    if (!isQuestSessionActive || abandonOpen || resultModal || questFinishingRef.current) {
      return undefined;
    }
    if (dwellSecondsLeft <= 0) return undefined;

    const ticker = window.setInterval(() => {
      setDwellSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => window.clearInterval(ticker);
  }, [isQuestSessionActive, abandonOpen, resultModal, dwellSecondsLeft]);

  useEffect(() => {
    if (!isQuestSessionActive || resultModal) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isQuestSessionActive, resultModal]);

  const closeResultModal = useCallback(() => {
    setResultModal(null);
    resetQuestRuntimeState();
    setActiveSessionMode(TRAINING_MODE_FREE);
    setWordMarks({});
    setPhase("setup");
    setSessionWords([]);
    setSessionIndex(0);
    setSessionQueue([]);
    setIsPaused(false);
    onPhaseChange?.("setup");
  }, [onPhaseChange, resetQuestRuntimeState]);

  const stopSession = useCallback(() => {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
    resetQuestRuntimeState();
    setResultModal(null);
    setActiveSessionMode(TRAINING_MODE_FREE);
    setWordMarks({});
    setPhase("setup");
    setSessionWords([]);
    setSessionIndex(0);
    setSessionQueue([]);
    setIsPaused(false);
    onPhaseChange?.("setup");
  }, [onPhaseChange, resetQuestRuntimeState]);

  const requestLeave = useCallback(
    (afterLeave) => {
      const resume = typeof afterLeave === "function" ? afterLeave : null;
      if (isQuestSessionActive && !resultModal) {
        pendingLeaveRef.current = resume;
        setAbandonOpen(true);
        return true;
      }
      if (!resume) stopSession();
      return false;
    },
    [isQuestSessionActive, resultModal, stopSession]
  );

  const cancelAbandon = useCallback(() => {
    pendingLeaveRef.current = null;
    setAbandonOpen(false);
  }, []);

  const confirmAbandon = useCallback(() => {
    const resume = pendingLeaveRef.current;
    pendingLeaveRef.current = null;
    setAbandonOpen(false);
    stopSession();
    resume?.();
  }, [stopSession]);

  useLayoutEffect(() => {
    if (!embedded || !onRegisterStopSession) return undefined;
    onRegisterStopSession(requestLeave);
    return () => onRegisterStopSession(null);
  }, [embedded, onRegisterStopSession, requestLeave]);

  const togglePause = () => {
    setIsPaused((prev) => !prev);
  };

  const currentWord = phase === "session" ? sessionWords[sessionIndex] : null;
  const currentWordGrade = currentWord ? getWordEwssDisplayGrade(currentWord) : null;
  const currentWordLastSpelling = currentWord ? getLastSpellingGiven(currentWord.stats) : null;
  const currentWordDisplay = splitMotDisplay(currentWord?.mot);
  const currentWordMark = currentWord ? wordMarks[currentWord.mot] : null;

  const handleTrainingGroupementChange = useCallback(
    async (nextGroupementId) => {
      const mot = currentWord?.mot;
      if (!mot || groupementBusy) return;
      const previous = currentWord.groupementId || "";
      const next = nextGroupementId || "";
      if (next === previous) return;

      const patchList = (list) =>
        list.map((word) =>
          word.mot === mot ? { ...word, groupementId: next || null } : word
        );
      setSessionWords(patchList);
      setWords(patchList);
      setGroupementBusy(true);
      try {
        await assignWordGroupement(mot, next || null);
      } catch (error) {
        const revert = (list) =>
          list.map((word) =>
            word.mot === mot ? { ...word, groupementId: previous || null } : word
          );
        setSessionWords(revert);
        setWords(revert);
        alert(error.message || "Impossible d'enregistrer le groupement thématique.");
      } finally {
        setGroupementBusy(false);
      }
    },
    [currentWord, groupementBusy]
  );
  const isCurrentFavorite =
    currentWordMark?.favori ?? currentWord?.categorie?.coeur === 1;
  const isCurrentPriority =
    currentWordMark?.prioritaire ?? currentWord?.categorie?.top20 === 1;
  const currentWordMotsCaches = currentWord ? getTrainingMotsCaches(currentWord) : [];
  const definitionToShow = currentWord
    ? hideTrainingWord
      ? maskDefinition(currentWord.definition, currentWordMotsCaches)
      : currentWord.definition
    : "";
  const extraitToShow = currentWord
    ? hideTrainingWord
      ? maskDefinition(currentWord.extrait, currentWordMotsCaches)
      : currentWord.extrait
    : "";

  useEffect(() => {
    const mot = currentWord?.mot;
    if (phase !== "session" || !mot) {
      setCurrentIllustration(null);
      return undefined;
    }

    let cancelled = false;
    const cache = illustrationCacheRef.current;

    const loadIllustration = async (targetMot, { apply } = {}) => {
      if (!targetMot) return;
      if (cache.has(targetMot)) {
        if (apply && !cancelled) {
          setCurrentIllustration(cache.get(targetMot));
        }
        return;
      }

      try {
        const dataUrl = await fetchWordIllustration(targetMot);
        cache.set(targetMot, dataUrl);
        if (apply && !cancelled) {
          setCurrentIllustration(dataUrl);
        }
      } catch {
        cache.set(targetMot, null);
        if (apply && !cancelled) {
          setCurrentIllustration(null);
        }
      }
    };

    if (cache.has(mot)) {
      setCurrentIllustration(cache.get(mot));
    } else {
      setCurrentIllustration(null);
      loadIllustration(mot, { apply: true });
    }

    const nextMot = sessionWords[(sessionIndex + 1) % sessionWords.length]?.mot;
    const prevMot =
      sessionWords[(sessionIndex - 1 + sessionWords.length) % sessionWords.length]?.mot;
    if (nextMot && nextMot !== mot) {
      loadIllustration(nextMot);
    }
    if (prevMot && prevMot !== mot && prevMot !== nextMot) {
      loadIllustration(prevMot);
    }

    return () => {
      cancelled = true;
    };
  }, [phase, currentWord?.mot, sessionIndex, sessionWords]);

  const patchWordMark = useCallback((mot, patch) => {
    setWordMarks((prev) => ({
      ...prev,
      [mot]: { ...prev[mot], ...patch },
    }));
  }, []);

  const applyWordCategory = useCallback((mot, { coeur, top20 }) => {
    setWords((prev) =>
      prev.map((word) => {
        if (word.mot !== mot) return word;
        return {
          ...word,
          categorie: {
            ...(word.categorie || {}),
            ...(coeur !== undefined ? { coeur: coeur ? 1 : 0 } : {}),
            ...(top20 !== undefined ? { top20: top20 ? 1 : 0 } : {}),
          },
        };
      })
    );
  }, []);

  const handleFavoriteChange = useCallback(
    async (checked) => {
      const mot = sessionWords[sessionIndex]?.mot;
      if (!mot) return;

      patchWordMark(mot, { favori: checked });
      try {
        const res = await fetch(`${API_URL}/mots/${encodeURIComponent(mot)}/favori`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ favori: checked }),
        });
        const result = await res.json().catch(() => ({}));
        if (!res.ok) {
          if (result?.message && !isQuestSessionActive) window.alert(result.message);
          patchWordMark(mot, { favori: !checked });
          return;
        }
        applyWordCategory(mot, { coeur: result.favori ?? checked });
        if (typeof result.prioritaire === "boolean") {
          patchWordMark(mot, { prioritaire: result.prioritaire });
        }
      } catch (err) {
        console.error("Erreur maj favori :", err);
        patchWordMark(mot, { favori: !checked });
      }
    },
    [sessionWords, sessionIndex, patchWordMark, applyWordCategory, isQuestSessionActive]
  );

  const handlePriorityChange = useCallback(
    async (checked) => {
      const mot = sessionWords[sessionIndex]?.mot;
      if (!mot) return;

      patchWordMark(mot, { prioritaire: checked });
      try {
        const res = await fetch(`${API_URL}/mots/${encodeURIComponent(mot)}/prioritaire`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ prioritaire: checked }),
        });
        const result = await res.json().catch(() => ({}));
        if (!res.ok) {
          if (result?.message && !isQuestSessionActive) window.alert(result.message);
          patchWordMark(mot, { prioritaire: !checked });
          return;
        }
        applyWordCategory(mot, { top20: result.prioritaire ?? checked });
        if (typeof result.favori === "boolean") {
          patchWordMark(mot, { favori: result.favori });
        }
      } catch (err) {
        console.error("Erreur maj prioritaire :", err);
        patchWordMark(mot, { prioritaire: !checked });
      }
    },
    [sessionWords, sessionIndex, patchWordMark, applyWordCategory, isQuestSessionActive]
  );

  const handleHideTrainingWordChange = useCallback((checked) => {
    setHideTrainingWord(checked);
    writeTrainingHideWord(checked);
  }, []);

  useEffect(() => {
    if (phase !== "session") return undefined;

    const handleTrainingShortcut = (event) => {
      if (event.repeat || event.ctrlKey || event.metaKey || event.altKey) return;
      if (abandonOpen || resultModal) return;

      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT" ||
          target.isContentEditable)
      ) {
        return;
      }

      const key = typeof event.key === "string" ? event.key.toLowerCase() : "";
      if (key === "f") {
        event.preventDefault();
        if (currentWord) handleFavoriteChange(!isCurrentFavorite);
        return;
      }
      if (key === "p") {
        event.preventDefault();
        if (currentWord) handlePriorityChange(!isCurrentPriority);
        return;
      }
      if (key === "c") {
        event.preventDefault();
        handleHideTrainingWordChange(!hideTrainingWord);
      }
    };

    window.addEventListener("keydown", handleTrainingShortcut);
    return () => window.removeEventListener("keydown", handleTrainingShortcut);
  }, [
    phase,
    abandonOpen,
    resultModal,
    currentWord,
    isCurrentFavorite,
    isCurrentPriority,
    hideTrainingWord,
    handleFavoriteChange,
    handlePriorityChange,
    handleHideTrainingWordChange,
  ]);

  const canNavigateWords = !isQuestSessionActive && sessionWords.length > 1;
  const questWordsRemaining = Math.max(0, sessionWords.length - sessionIndex);
  const questOnLastWord = sessionWords.length > 0 && sessionIndex >= sessionWords.length - 1;
  const dwellUnit = dwellSecondsLeft > 1 ? "secondes" : "seconde";
  const dwellMessage = questOnLastWord
    ? `Vous pourrez valider la session dans ${dwellSecondsLeft} ${dwellUnit}`
    : `Vous pourrez découvrir le prochain mot dans ${dwellSecondsLeft} ${dwellUnit}`;
  const playDisabled = isQuestModeSelected
    ? !questSessionAvailable || words.length === 0
    : filteredCount === 0;
  const ewssGradeFiltersDisabled = setup.neverPlayed || setup.withoutEwssScore;
  const noEwssGameCountDisabled = !setup.withoutEwssScore || setup.neverPlayed;

  const patchEwssFlag = useCallback((patch) => {
    setSetup((prev) => {
      const next = { ...prev, ...patch };

      if (patch.neverPlayed === true) {
        next.withoutEwssScore = false;
        next.noEwssGameCount = "";
      }

      if (patch.withoutEwssScore === true) {
        next.neverPlayed = false;
        next.noEwssGameCount = TRAINING_DEFAULT_NO_EWSS_GAME_COUNT;
      }

      if (patch.withoutEwssScore === false) {
        next.noEwssGameCount = "";
      }

      if (next.neverPlayed || next.withoutEwssScore) {
        next.exactGrade = "";
        next.belowGrade = "";
        next.aboveGrade = "";
      }

      return next;
    });
  }, []);

  const patchNoEwssGameCount = useCallback((noEwssGameCount) => {
    setSetup((prev) => {
      if (!prev.withoutEwssScore) return prev;
      return { ...prev, noEwssGameCount };
    });
  }, []);

  const patchExactGrade = useCallback((exactGrade) => {
    setSetup((prev) => {
      const next = {
        ...prev,
        exactGrade,
        neverPlayed: false,
        withoutEwssScore: false,
        noEwssGameCount: "",
      };
      if (exactGrade) {
        next.belowGrade = "";
        next.aboveGrade = "";
      }
      return next;
    });
  }, []);

  const patchBelowGrade = useCallback((belowGrade) => {
    setSetup((prev) => {
      const next = {
        ...prev,
        belowGrade,
        neverPlayed: false,
        withoutEwssScore: false,
        noEwssGameCount: "",
      };
      if (belowGrade) {
        next.exactGrade = "";
        next.aboveGrade = "";
      }
      return next;
    });
  }, []);

  const patchAboveGrade = useCallback((aboveGrade) => {
    setSetup((prev) => {
      const next = {
        ...prev,
        aboveGrade,
        neverPlayed: false,
        withoutEwssScore: false,
        noEwssGameCount: "",
      };
      if (aboveGrade) {
        next.exactGrade = "";
        next.belowGrade = "";
      }
      return next;
    });
  }, []);

  return (
    <div className={`entrainement${embedded ? " entrainement--embedded" : ""}`}>
      <div className={`entrainement__panel${embedded ? " entrainement__panel--embedded" : ""}`}>
        {!embedded && (
          <button type="button" className="entrainement__close" onClick={onClose} aria-label="Fermer">
            ×
          </button>
        )}

        {!embedded && (
          <h2 className="entrainement__title">Entraînement au vocabulaire</h2>
        )}

        {loading && !loadError ? <EntrainementSetupSkeleton /> : null}
        {loadError && <p className="entrainement__status entrainement__status--error">{loadError}</p>}

        {!loading && !loadError && phase === "setup" && (
          <div className="entrainement-setup">
            <div className="entrainement-setup__intro">
              <h2 className="entrainement-setup__heading">
                {isQuestModeSelected ? "Session de quête" : "Entraînement libre"}
              </h2>
              {isQuestModeSelected ? (
                <p className="entrainement-setup__quest-blurb">
                  40 mots, au moins 10 secondes sur chacun. La session est validée lorsque les 40 mots ont été parcourus.
                </p>
              ) : null}
            </div>
            <div
              className={`entrainement-setup__board${
                isQuestModeSelected ? " entrainement-setup__board--quest" : ""
              }`}
            >
              {!isQuestModeSelected ? (
                <>
              <Quadrant title="Notes EWSS" className="entrainement-quadrant--tl">
                <div className="entrainement-ewss-grades">
                  <Field label="Note précise">
                    <EntrainementSelect
                      tiny
                      value={setup.exactGrade}
                      disabled={ewssGradeFiltersDisabled}
                      onChange={patchExactGrade}
                      ariaLabel="Note précise"
                      options={[
                        { value: "", label: "— Toutes —" },
                        ...EWSS_GRADES.map((grade) => ({ value: grade, label: grade })),
                      ]}
                    />
                  </Field>
                  <Field label="Inférieur ou égal à">
                    <EntrainementSelect
                      tiny
                      value={setup.belowGrade}
                      disabled={ewssGradeFiltersDisabled}
                      onChange={patchBelowGrade}
                      ariaLabel="Inférieur ou égal à"
                      options={[
                        { value: "", label: "— Aucun seuil —" },
                        ...EWSS_GRADES.map((grade) => ({ value: grade, label: grade })),
                      ]}
                    />
                  </Field>
                  <Field label="Supérieur ou égal à">
                    <EntrainementSelect
                      tiny
                      value={setup.aboveGrade}
                      disabled={ewssGradeFiltersDisabled}
                      onChange={patchAboveGrade}
                      ariaLabel="Supérieur ou égal à"
                      options={[
                        { value: "", label: "— Aucun seuil —" },
                        ...EWSS_GRADES.map((grade) => ({ value: grade, label: grade })),
                      ]}
                    />
                  </Field>
                </div>
                <div className="entrainement-chip-grid entrainement-chip-grid--ewss-flags">
                  <CheckOption
                    label="Mots jamais joués"
                    checked={setup.neverPlayed}
                    onChange={(checked) => patchEwssFlag({ neverPlayed: checked })}
                  />
                  <CheckOption
                    label="Mots sans score EWSS"
                    checked={setup.withoutEwssScore}
                    onChange={(checked) => patchEwssFlag({ withoutEwssScore: checked })}
                  />
                </div>
                <EntrainementSelect
                  tiny
                  className="entrainement-ewss-game-count"
                  value={setup.noEwssGameCount || TRAINING_DEFAULT_NO_EWSS_GAME_COUNT}
                  disabled={
                    noEwssGameCountDisabled ||
                    Boolean(setup.exactGrade) ||
                    Boolean(setup.belowGrade) ||
                    Boolean(setup.aboveGrade)
                  }
                  onChange={patchNoEwssGameCount}
                  ariaLabel="Mots sans score EWSS selon le nombre de parties jouées"
                  options={TRAINING_NO_EWSS_GAME_COUNT_OPTIONS}
                />
              </Quadrant>

              <Quadrant title="Catégories" className="entrainement-quadrant--tr">
                <div className="entrainement-chip-grid entrainement-chip-grid--categories">
                  <CheckOption
                    label="Mots mal restitués à la dernière parution"
                    checked={setup.lastAppearanceFailed}
                    onChange={(checked) => patchSetup({ lastAppearanceFailed: checked })}
                    full
                  />
                  <div className="entrainement-consecutive-fails">
                    <CheckOption
                      label="Nombre d'échec successif."
                      checked={setup.consecutiveFails}
                      onChange={(checked) => patchSetup({ consecutiveFails: checked })}
                    />
                    <EntrainementSelect
                      compact
                      value={setup.consecutiveFailsCount}
                      disabled={!setup.consecutiveFails}
                      onChange={(next) =>
                        patchSetup({ consecutiveFailsCount: Number(next) })
                      }
                      ariaLabel="Seuil d'échecs successifs"
                      options={TRAINING_CONSECUTIVE_FAIL_OPTIONS.map((count) => ({
                        value: String(count),
                        label: `${count} échec${count > 1 ? "s" : ""} successif${count > 1 ? "s" : ""}`,
                      }))}
                    />
        </div>
                  {CATEGORY_OPTIONS.map(({ key, label }) => (
                    <CheckOption
                      key={key}
                      label={label}
                      checked={setup.categories[key]}
                      onChange={(checked) => toggleCategory(key, checked)}
                    />
                  ))}
                  <div className="entrainement-lecture-row entrainement-illustration-filter">
                    <Field label="Illustration">
                      <EntrainementSelect
                        compact
                        value={setup.illustration || ""}
                        onChange={(next) => patchSetup({ illustration: next })}
                        ariaLabel="Filtrer par illustration"
                        options={ILLUSTRATION_FILTER_OPTIONS}
                      />
                    </Field>
                  </div>
                </div>
              </Quadrant>

              <Quadrant title="Source" className="entrainement-quadrant--bl">
                <Field label="Ouvrage">
                  <EntrainementSelect
                    value={setup.livre}
                    onChange={(next) => patchSetup({ livre: next })}
                    ariaLabel="Ouvrage"
                    options={[
                      { value: "", label: "— Tous —" },
                      ...livreOptions.map((livre) => ({ value: livre, label: livre })),
                    ]}
                  />
                </Field>
                <Field label="Auteur">
                  <EntrainementSelect
                    value={setup.auteur}
                    onChange={(next) => patchSetup({ auteur: next })}
                    ariaLabel="Auteur"
                    options={[
                      { value: "", label: "— Tous —" },
                      ...auteurOptions.map((auteur) => ({ value: auteur, label: auteur })),
                    ]}
                  />
                </Field>
                <Field label="Année de capture">
                  <EntrainementSelect
                    value={setup.captureYear}
                    onChange={(next) => patchSetup({ captureYear: next })}
                    ariaLabel="Année de capture"
                    options={[
                      { value: "", label: "— Toutes —" },
                      ...yearOptions.map((year) => ({ value: year, label: String(year) })),
                    ]}
                  />
                </Field>
              </Quadrant>

              <Quadrant hideTitle className="entrainement-quadrant--br">
                <div className="entrainement-quadrant-rubric">
                  <h4 className="entrainement-quadrant-rubric__title">Potions</h4>
                  <div className="entrainement-potions-used">
                    <CheckOption
                      label="Potions utilisées"
                      checked={setup.potionsUsed}
                      onChange={(checked) => patchSetup({ potionsUsed: checked })}
                    />
                    <EntrainementSelect
                      compact
                      value={setup.potionsUsedFilter}
                      disabled={!setup.potionsUsed}
                      onChange={(next) => patchSetup({ potionsUsedFilter: next })}
                      ariaLabel="Type de potion utilisée"
                      options={TRAINING_POTION_AID_FILTER_OPTIONS}
                    />
                  </div>
                </div>

                <div className="entrainement-quadrant-rubric">
                  <h4 className="entrainement-quadrant-rubric__title">
                    Regroupement thématique
                  </h4>
                  <div className="entrainement-groupement-row">
                    <EntrainementSelect
                      compact
                      value={setup.groupement || ""}
                      onChange={(next) =>
                        patchSetup({
                          groupement: next,
                          ...(next !== "only" ? { groupementId: "" } : {}),
                        })
                      }
                      ariaLabel="Filtrer les mots affiliés à un regroupement"
                      options={GROUPEMENT_MODE_OPTIONS}
                    />
                    {setup.groupement === "only" ? (
                      <EntrainementSelect
                        compact
                        value={setup.groupementId || ""}
                        onChange={(next) => patchSetup({ groupementId: next })}
                        ariaLabel="Choisir une thématique"
                        options={[
                          { value: "", label: "Toutes les thématiques" },
                          ...groupements.map((item) => ({
                            value: item.groupementId,
                            label: String(item.title || "").trim() || "Sans titre",
                          })),
                        ]}
                      />
                    ) : null}
                  </div>
                </div>

                <div className="entrainement-quadrant-rubric entrainement-quadrant-rubric--played-count">
                  <h4 className="entrainement-quadrant-rubric__title">Nombre de fois joués</h4>
                  <div className="entrainement-played-count-range">
                    <input
                      type="number"
                      className="entrainement-input entrainement-played-count-range__input"
                      value={setup.playedCountMin}
                      min={0}
                      max={PLAYED_COUNT_FILTER_MAX}
                      step={1}
                      inputMode="numeric"
                      placeholder="Min"
                      onChange={(e) => patchSetup({ playedCountMin: e.target.value })}
                      aria-label="Minimum de parties jouées"
                    />
                    <span className="entrainement-played-count-range__sep">à</span>
                    <input
                      type="number"
                      className="entrainement-input entrainement-played-count-range__input"
                      value={setup.playedCountMax}
                      min={0}
                      max={PLAYED_COUNT_FILTER_MAX}
                      step={1}
                      inputMode="numeric"
                      placeholder="Max"
                      onChange={(e) => patchSetup({ playedCountMax: e.target.value })}
                      aria-label="Maximum de parties jouées"
                    />
                  </div>
                </div>

                <div className="entrainement-quadrant-rubric entrainement-quadrant-rubric--lecture">
                  <h4 className="entrainement-quadrant-rubric__title">Lecture</h4>
                  <div className="entrainement-lecture-row">
                    <Field label="Vitesse de défilement">
                      <EntrainementSelect
                        compact
                        value={setup.speedSeconds}
                        onChange={(next) =>
                          patchSetup({ speedSeconds: Number(next) })
                        }
                        ariaLabel="Vitesse de défilement"
                        options={TRAINING_SPEED_OPTIONS.map((seconds) => ({
                          value: String(seconds),
                          label: `${seconds} s`,
                        }))}
                      />
                    </Field>
                    <Field label="Répétition">
                      <EntrainementSelect
                        compact
                        value={setup.withRepetition ? "yes" : "no"}
                        onChange={(next) =>
                          patchSetup({ withRepetition: next === "yes" })
                        }
                        ariaLabel="Mode de répétition"
                        options={[
                          { value: "no", label: "Sans répétition" },
                          { value: "yes", label: "Avec répétition" },
                        ]}
                      />
                    </Field>
                  </div>
                </div>
              </Quadrant>
                </>
              ) : null}

              <button
                type="button"
                className="entrainement-setup__play"
                onClick={startSession}
                disabled={playDisabled}
                aria-label="Démarrer l'entraînement"
              >
                <Lottie
                  lottieRef={lottieRef}
                  animationData={playButtonAnimation}
                  loop
                  className="entrainement-setup__play-lottie"
                />
          </button>
        </div>

            <p className="entrainement-setup__count">
              {isQuestModeSelected ? (
                <>
                  <span className="entrainement-setup__count-value">40</span> mots pour la session
                  de quête
                </>
              ) : (
                <>
                  <span className="entrainement-setup__count-value">{filteredCount}</span>{" "}
                  {filteredCount <= 1 ? "mot correspondant" : "mots correspondants"}
                </>
              )}
            </p>
          </div>
        )}

        {!loading && !loadError && phase === "session" && (
          <div
            className={`entrainement-session${
              isQuestSessionActive ? " entrainement-session--quest" : ""
            }`}
          >
            <div className="entrainement-session__header">
              <div className="entrainement-session__mark entrainement-session__mark--favorite">
                <span className="entrainement-session__mark-label">Mot favori</span>
                <ToggleSwitch
                  compact
                  className="entrainement-session__mark-switch"
                  checked={Boolean(isCurrentFavorite)}
                  onChange={handleFavoriteChange}
                  disabled={!currentWord}
                />
      </div>

              {isQuestSessionActive ? (
                <div className="entrainement-session__quest-timer" aria-label="Mots restants">
                  <CountDownTimer
                    key={`quest-session-${sessionWords[0]?.mot || "words"}`}
                    displayValue={questWordsRemaining}
                    displayLimit={Math.max(1, sessionWords.length)}
                    warningThreshold={-1}
                    alertThreshold={-1}
                  />
                </div>
              ) : (
                <div className="entrainement-session__laurel" aria-hidden={!currentWord}>
                  <img src="/761364_ornaments.svg" alt="" />
                  <div
                    className={`entrainement-session__grade${
                      currentWordGrade ? "" : " entrainement-session__grade--na"
                    }`}
                  >
                    {currentWordGrade ? renderGradeInLaurel(currentWordGrade) : "—"}
                  </div>
                </div>
              )}

              <div className="entrainement-session__mark entrainement-session__mark--priority">
                <ToggleSwitch
                  compact
                  className="entrainement-session__mark-switch"
                  checked={Boolean(isCurrentPriority)}
                  onChange={handlePriorityChange}
                  disabled={!currentWord}
                />
                <span className="entrainement-session__mark-label">Mot prioritaire</span>
              </div>
            </div>

            {currentWord ? (
              <div className="entrainement-session__word">
                <TrainingSessionMotRow
                  hideTrainingWord={hideTrainingWord}
                  onHideTrainingWordChange={handleHideTrainingWordChange}
                  currentWordDisplay={currentWordDisplay}
                  groupements={groupements}
                  groupementId={currentWord?.groupementId || ""}
                  groupementBusy={groupementBusy}
                  onGroupementChange={handleTrainingGroupementChange}
                />
                <div
                  className={[
                    "entrainement-session__details",
                    currentIllustration
                      ? "entrainement-session__details--with-illustration"
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <div className="entrainement-session__details-copy">
                    <TrainingSessionWordDetails
                      hideTrainingWord={hideTrainingWord}
                      definitionToShow={definitionToShow}
                      extraitToShow={extraitToShow}
                      currentWord={currentWord}
                      currentWordLastSpelling={currentWordLastSpelling}
                    />
                  </div>
                  {currentIllustration ? (
                    <div className="entrainement-session__illustration">
                      <img
                        src={currentIllustration}
                        alt=""
                        className="entrainement-session__illustration-image"
                        draggable={false}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            ) : (
              <p className="entrainement__status">Aucun mot à afficher.</p>
            )}

            <div className="entrainement-session__footer">
              <p className="entrainement-session__info">
                {isQuestSessionActive ? (
                  <>
                    {sessionWords.length} mots · session de quête
                  </>
                ) : (
                  <>
                    {sessionWords.length} mot{sessionWords.length > 1 ? "s" : ""} ·{" "}
                    {setup.speedSeconds} s ·{" "}
                    {setup.withRepetition ? "avec répétition" : "sans répétition"}
                  </>
                )}
              </p>
              {isQuestSessionActive ? (
                dwellSecondsLeft > 0 ? (
                  <p className="entrainement-session__presence">{dwellMessage}</p>
                ) : (
                  <div className="entrainement-session__controls">
                    <button
                      type="button"
                      className="entrainement-session__nav"
                      onClick={goToNextQuestWord}
                      aria-label={questOnLastWord ? "Terminer la session" : "Mot suivant"}
                    >
                      <span>{questOnLastWord ? "Terminer" : "Suivant"}</span>
                      <ChevronRight size={16} strokeWidth={2.3} aria-hidden="true" />
                    </button>
                  </div>
                )
              ) : null}
              {!isQuestSessionActive ? (
                <div className="entrainement-session__controls">
                  <button
                    type="button"
                    className="entrainement-session__nav"
                    onClick={goToPreviousWord}
                    disabled={!canNavigateWords}
                    aria-label="Mot précédent"
                  >
                    <ChevronLeft size={16} strokeWidth={2.3} aria-hidden="true" />
                    <span>Précédent</span>
                  </button>
                  <button
                    type="button"
                    className="entrainement-session__pause"
                    onClick={togglePause}
                    aria-label={isPaused ? "Reprendre le défilement" : "Mettre en pause"}
                  >
                    {isPaused ? (
                      <Play size={18} strokeWidth={2.4} />
                    ) : (
                      <Pause size={18} strokeWidth={2.4} />
                    )}
                  </button>
                  <button
                    type="button"
                    className="entrainement-session__nav"
                    onClick={goToNextWord}
                    disabled={!canNavigateWords}
                    aria-label="Mot suivant"
                  >
                    <span>Suivant</span>
                    <ChevronRight size={16} strokeWidth={2.3} aria-hidden="true" />
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>

      {abandonOpen ? (
        <div
          className="quest-training-result"
          role="dialog"
          aria-modal="true"
          aria-labelledby="quest-training-abandon-title"
        >
          <div className="quest-training-result__card">
            <h3 id="quest-training-abandon-title" className="quest-training-result__title">
              Session non terminée
            </h3>
            <p className="quest-training-result__message">
              Si vous quittez maintenant, cette session ne sera pas validée. Vous devrez la
              recommencer depuis le premier mot.
            </p>
            <div className="quest-training-result__actions">
              <button type="button" className="quest-training-result__stay" onClick={cancelAbandon}>
                Rester
              </button>
              <button type="button" className="quest-training-result__ok" onClick={confirmAbandon}>
                Quitter
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <QuestTrainingResultModal open={Boolean(resultModal)} onConfirm={closeResultModal} />
    </div>
  );
}
